/**
 * Single source of truth for brand / business metadata.
 *
 * Used by:
 *   - SEO meta tags (title, description, OG, Twitter)
 *   - JSON-LD structured data (LocalBusiness / Physiotherapist)
 *   - Site header / footer copy
 *   - llms.txt / sitemap.xml generation
 *
 * Geo coordinates are approximate (Santa Cruz de Tenerife centre) — replace
 * with the exact ones from Google Maps once confirmed so the LocalBusiness
 * schema resolves to the real pin.
 */

export const brand = {
  id: 'tenerfisio',
  name: 'TenerFisio',
  legalName: 'Isaac Hernández Negrín',
  legalNif: '79065715H',
  tagline: {
    es: 'Fisioterapia · Pilates · Masajes en Santa Cruz de Tenerife',
    en: 'Physiotherapy · Pilates · Massage in Santa Cruz de Tenerife',
  },
  description: {
    es: 'Centro de fisioterapia en Santa Cruz de Tenerife dirigido por Isaac Hernández Negrín (col. 1905). Tratamientos personalizados de fisioterapia, pilates terapéutico y masaje para tu salud y bienestar.',
    en: 'Physiotherapy clinic in Santa Cruz de Tenerife led by Isaac Hernández Negrín (col. 1905). Personalised physiotherapy, therapeutic pilates and massage treatments for your health and wellbeing.',
  },
  url: 'https://www.tenerfisio.com',
  googleMapsUrl:
    'https://www.google.com/maps/place/Tenerfisio+-+Centro+de+Fisioterapia,+Pilates+y+Masajes/@28.461969,-16.2536229,17z',
  logo: '/og-image.png', // fallback OG image; replace with branded banner later
  practitioner: {
    name: 'Isaac Hernández Negrín',
    licenseNumber: 'col. 1905',
  },
  contact: {
    landline: '+34922065151',
    landlineDisplay: '+34 922 06 51 51',
    mobile: '+34680966978',
    mobileDisplay: '+34 680 966 978',
    phone: '+34922065151', // primary for schema/SEO
    phoneDisplay: '+34 922 06 51 51',
    whatsapp: 'https://wa.me/34680966978',
    email: 'info@tenerfisio.com',
    facebook: 'https://www.facebook.com/Tenerfisio',
    instagram: 'https://www.instagram.com/tenerfisio/',
    youtube: 'https://www.youtube.com/channel/UCXnLmAPbwRdKQ0a9a8goL-Q/videos',
  },
  address: {
    street: 'C. de Fernández Navarro, 17',
    locality: 'Santa Cruz de Tenerife',
    region: 'Santa Cruz de Tenerife',
    postalCode: '38003',
    country: 'ES',
  },
  geo: {
    latitude: 28.461969,
    longitude: -16.2536229,
  },
  locales: ['es', 'en'] as const,
  defaultLocale: 'es' as const,
  services: [
    { id: 'fisioterapia', es: 'Fisioterapia', en: 'Physiotherapy' },
    { id: 'pilates', es: 'Pilates', en: 'Pilates' },
    { id: 'masajes', es: 'Masajes', en: 'Massage' },
  ],
  openingHours: [
    { days: ['Mo', 'Tu', 'We', 'Th', 'Fr'], opens: '08:00', closes: '14:00' },
    { days: ['Mo', 'Tu', 'We', 'Th', 'Fr'], opens: '15:00', closes: '22:00' },
  ],
  aggregateRating: {
    ratingValue: 4.9,
    reviewCount: 318,
  },
  bookingUrl:
    'https://app.reasoningphysios.com/CitaOnline/citaLogin.aspx?clinica=781&codigoImgLog=9067DD06B92DB998DAD28BD5155381764C61203D',
  customerPortalUrl: 'https://tenerfisio.weappfit.com',
} as const;

export type Locale = (typeof brand.locales)[number];
export const isLocale = (x: string): x is Locale =>
  (brand.locales as readonly string[]).includes(x);
